/**
 * Environment validation.
 *
 * Fails loudly at module load rather than producing a client that 401s on every
 * request with no explanation. A missing key is a setup mistake, and the error
 * should say exactly which one and where to get it.
 */

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `Missing ${name}.\n\n` +
        `Copy .env.example to .env.local and fill in the values from your Supabase ` +
        `dashboard (Project Settings → API), then restart the dev server.`,
    )
  }
  return value.trim()
}

export const SUPABASE_URL = required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL)

export const SUPABASE_ANON_KEY = required(
  'VITE_SUPABASE_ANON_KEY',
  import.meta.env.VITE_SUPABASE_ANON_KEY,
)

if (!/^https:\/\/[^/]+$|^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(SUPABASE_URL)) {
  // Accept hosted (https://<ref>.supabase.co), self-hosted custom domains
  // (https://api.example.com), and the local CLI stack (http://localhost:54321).
  // We only flag the shapes that actually break: a trailing slash, a path, or a
  // non-loopback http/copied dashboard URL, which produce confusing CORS errors
  // rather than an obvious failure.
  console.warn(
    `VITE_SUPABASE_URL="${SUPABASE_URL}" does not look like a base URL ` +
      `(expected https://host, or http://localhost:<port> for local dev, ` +
      `with no trailing slash and no path).`,
  )
}

/**
 * The `role` claim out of a JWT payload, or null if it is not a JWT.
 *
 * Reading the payload is the only reliable test. The previous check looked for
 * the literal text "service_role" in the key, which is never there: a legacy
 * service_role key is a JWT whose payload is base64url, so the role appears as
 * `InNlcnZpY2Vfcm9sZSI`, and a modern `sb_secret_…` key is opaque. It therefore
 * fired for exactly zero real keys — a guard that could not guard.
 */
function jwtRole(key: string): string | null {
  const parts = key.split('.')
  if (parts.length !== 3 || !parts[1]) return null
  try {
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    const role: unknown = (JSON.parse(payload) as { role?: unknown }).role
    return typeof role === 'string' ? role : null
  } catch {
    return null
  }
}

if (jwtRole(SUPABASE_ANON_KEY) === 'service_role' || SUPABASE_ANON_KEY.startsWith('sb_secret_')) {
  // The service_role key bypasses Row Level Security entirely. In a browser
  // bundle it hands every visitor unrestricted access to the whole database.
  throw new Error(
    'VITE_SUPABASE_ANON_KEY appears to be a service_role key. That key bypasses ' +
      'Row Level Security and must never ship to the browser. Use the anon / ' +
      'publishable key instead, and rotate the leaked service_role key now.',
  )
}
